import { action, computed, observable } from 'mobx';
import { isValidEmail } from 'app/utils/validators';
import TicketsBaseFormStore from 'modules/ticketPublicPage/stores/common/form';
import view from 'modules/ticketPublicPage/stores/vouchers/view';
import facility from 'app/facility';

export class VouchersFormStore extends TicketsBaseFormStore {
  constructor(view) {
    super();
    this.view = view;
    this.setFormDefaults();
  }
  CONTACT_DETAILS = 'Contact Details';
  PAYMENT_DETAILS = 'Payment Details';
  @observable accessor isOwnValue = false;

  @computed get showBackButton() {
    return ![this.INITIAL, this.CONFIRMATION].includes(this.step);
  }

  @computed get hideBuyButton() {
    return (
      (this.formValues.get('payment_type') === this.paymentTypes.paypal &&
        this.step === this.PAYMENT_DETAILS) ||
      this.step === this.CONFIRMATION
    );
  }

  @computed get errorTexts() {
    let errorFields;
    switch (this.step) {
      case this.INITIAL:
        errorFields = ['price', 'sender', 'recipient', 'greeting_message'];
        break;
      case this.CONTACT_DETAILS:
        errorFields = [
          'email',
          'recipient_name',
          'recipient_surname',
          'name',
          'surname',
        ];
        break;
      default:
        errorFields = [];
    }
    return errorFields
      .map((field) => this.errors.get(field))
      .filter((err) => !!err)
      .reduce((r, c) => r.add(c), new Set());
  }

  @action setOwnValueClickedStatus = (isOwnValue) => {
    this.isOwnValue = isOwnValue;
  };

  @action onNextBtnClick = async () => {
    this.validate();
    if (this.hasErrors) return;
    switch (this.step) {
      case this.INITIAL:
        this.step = this.CONTACT_DETAILS;
        break;
      case this.CONTACT_DETAILS:
        this.step = this.PAYMENT_DETAILS;
        break;
      case this.PAYMENT_DETAILS:
        if (this.formValues.get('payment_type') === this.paymentTypes.bambora) {
          const price = Number(this.formValues.get('price'));
          await this.handleBamboraCheckout(price);
        }
        this.validatePayment();
        break;
      case this.CONFIRMATION:
        this.step = this.INITIAL;
        this.setFormDefaults();
        break;
    }
  };

  @action onBackBtnClick = () => {
    switch (this.step) {
      case this.CONTACT_DETAILS:
        this.step = this.INITIAL;
        break;
      case this.PAYMENT_DETAILS:
        this.step = this.CONTACT_DETAILS;
        break;
    }
    this.errors.clear();
  };

  @action setCustomFormDefaults() {
    this.formValues.set('price', this.view.defaultPrice);
    this.formValues.set('sender', '');
    this.formValues.set('recipient', '');
    this.formValues.set('recipient_surname', '');
    this.formValues.set('recipient_name', '');
    this.formValues.set('name', '');
    this.formValues.set('surname', '');
    this.formValues.set('email', '');
    this.formValues.set('owner_name', '');
    this.formValues.set('owner_email', '');
    this.formValues.set('newsletter_subscribe', false);
    this.isOwnValue = false;
  }

  @action validate = () => {
    let validateFields = [];
    switch (this.step) {
      case this.INITIAL:
        validateFields = ['price', 'sender', 'recipient', 'greeting_message'];
        break;
      case this.CONTACT_DETAILS:
        validateFields = [
          'recipient_name',
          'recipient_surname',
          'name',
          'surname',
          'email',
        ];
        break;
      case this.PAYMENT_DETAILS:
        break;
    }
    validateFields.forEach((field) => this.validateField(field));
  };

  @action validateField = (field) => {
    const value = this.formValues.get(field);
    const {
      min_value: minValue,
      max_value: maxValue,
      individual_value_allowed: individualValueAllowed,
    } = this.view.activeObject;
    const { currencyShort } = facility.settings;
    switch (field) {
      case 'price':
        if (
          individualValueAllowed &&
          (this.isOwnValue || view.hasNoFixedValues)
        ) {
          if (value < minValue || value > maxValue)
            this.setError(
              field,
              i18n.sprintf(
                i18n.gettext('Please set a price between %s %s and %s %s.'),
                minValue,
                currencyShort,
                maxValue,
                currencyShort
              )
            );
        }
        if (parseFloat(value.toString().replace(',', '.')) % 1 !== 0) {
          this.setError(
            field,
            i18n.gettext('Please enter a smooth value. Thank you!')
          );
        }
        if (!value) {
          this.setError(field, i18n.gettext('Please input a price.'));
        }
        break;
      case 'email':
        if (!isValidEmail(value))
          this.setError(
            field,
            i18n.gettext('Please input a valid email address.')
          );
        break;
      case 'recipient':
        if (!value) {
          this.setError(field, i18n.gettext('Please add a recipient.'));
        }
        break;
      case 'sender':
        if (!value) {
          this.setError(field, i18n.gettext('Please add a sender.'));
        }
        break;
      case 'recipient_name':
        if (!value) {
          this.setError(
            field,
            i18n.gettext('Please add the recipients first name.')
          );
        }
        break;
      case 'recipient_surname':
        if (!value) {
          this.setError(
            field,
            i18n.gettext('Please add the recipients last name.')
          );
        }
        break;
      case 'name':
        if (!value) {
          this.setError(field, i18n.gettext('Please add your first name.'));
        }
        break;
      case 'surname':
        if (!value) {
          this.setError(field, i18n.gettext('Please add your last name.'));
        }
        break;
      default:
        if (!this.formValues.get(field) && field !== 'greeting_message')
          this.setError(field, i18n.gettext('Please fill in the field.'));
        break;
    }
  };

  convertFormToServer = () => {
    const converted = { ticket_id: this.view.activeObject.id };
    Array.from(this.formValues.keys()).map((key) => {
      switch (key) {
        case 'price':
          converted[key] = this.formValues
            .get(key)
            .toString()
            .replace(',', '.');
          break;
        default:
          converted[key] = this.formValues.get(key);
          break;
      }
    });
    return converted;
  };
}

const form = new VouchersFormStore(view);
export default form;
